// src/components/doctor/ConfirmActionDialog.jsx
//
// A small confirmation in the shared .dialog shell (used instead of
// window.confirm). Focus starts on Cancel — the confirming action here is
// destructive or hard to undo (closing a visit without a summary, marking a
// no-show) — and Escape cancels unless the action is running. A `problem`
// (e.g. the server's refusal) is shown inside the dialog and the dialog stays
// open.

import React, { useEffect, useRef } from 'react';
import useDialogFocus from '../../hooks/useDialogFocus';

export default function ConfirmActionDialog({
  title, children, confirmLabel, busyLabel = 'Working…', busy = false, problem = null, onCancel, onConfirm,
}) {
  const cancelRef = useRef(null);
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef);
  useEffect(() => { cancelRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);
  return (
    <div className="dialog-backdrop" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-action-title" aria-describedby="confirm-action-desc" ref={dialogRef}>
        <div className="dialog-title" id="confirm-action-title">{title}</div>
        <div className="dialog-sub" id="confirm-action-desc">{children}</div>
        {problem && <div className="alert alert-error" role="alert"><div className="grow">{problem}</div></div>}
        <div className="dialog-actions">
          <button type="button" ref={cancelRef} className="btn btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={onConfirm}>{busy ? busyLabel : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
